import { ConfigProvider, Layout } from 'antd';
import type { Locale } from 'antd/es/locale';
import enGB from 'antd/locale/en_GB';
import enUS from 'antd/locale/en_US';
import React, { type CSSProperties, useEffect } from 'react';

const antdLocale: Locale = (() => {
  try {
    const firstDay = (
      new Intl.Locale(navigator.language) as Intl.Locale & {
        weekInfo?: { firstDay: number };
      }
    ).weekInfo?.firstDay;
    return firstDay === 7 ? enUS : enGB;
  } catch {
    return enGB;
  }
})();
import { useSelector } from 'react-redux';
import { Outlet, Route, Routes } from 'react-router';

import { ChunkErrorBoundary } from '../components/chunk-error-boundary';
import KeyboardShortcutsHelp from '../components/keyboard-shortcuts';
import SidebarCRM from '../components/sidebar-crm';
import SidebarDownloader from '../components/sidebar-downloader';
import SidebarEbay from '../components/sidebar-ebay';
import SidebarMarketing from '../components/sidebar-marketing';
import SidebarOffer from '../components/sidebar-offer';
import SidebarOrders from '../components/sidebar-orders';
import SidebarPackets from '../components/sidebar-packets';
import SidebarReport from '../components/sidebar-report';
import SidebarSettings from '../components/sidebar-settings';
import SiderContentComponent from '../components/sider-content';
import Topbar from '../components/topbar';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import PrivateRoute from '../private-route';
import { type RootState } from '../reducers';
import { withdrawalReceivedCount } from '../reducers/withdrawal/reducer';
import type Socket from '../socket';
import AccountContainer from './account-container';
import BrandContainer from './brand-container';
import BrandDetailContainer from './brand-detail-container';
import CategoriesContainer from './categories-container';
import CategoryContainer from './category-container';
import CrmCustomerDetailContainer from './crm-customer-detail-container';
import CrmCustomersContainer from './crm-customers-container';
import CrmWatchdogContainer from './crm-watchdog-container';
import DashboardContainer from './dashboard-container';
import DiscountContainer from './discount-container';
import DownloaderContainer from './downloader-container';
import DownloaderProductContainer from './downloader-product-container';
import DownloaderProductsContainer from './downloader-products-container';
import DownloaderWarehouseCategories from './downloader-warehouse-categories';
import DownloaderWarehouseCategory from './downloader-warehouse-category';
import DownloaderWarehouseProduct from './downloader-warehouse-product';
import DownloaderWarehouseProducts from './downloader-warehouse-products';
import DownloaderWarehouseProperties from './downloader-warehouse-properties';
import DownloaderWarehouseProperty from './downloader-warehouse-property';
import EbayContainer from './ebay-container';
import EbayMarketplaceContainer from './ebay-marketplace-container';
import EbayMarketplacesContainer from './ebay-marketplaces-container';
import GiftRegistryContainer from './gift-registry-container';
import HomeContainer from './home-container';
import LoginContainer from './login-container';
import LogoutContainer from './logout-container';
import ManufacturersContainer from './manufacturers-container';
import MarketingConsentCampaignContainer from './marketing-consent-campaign-container';
import MessengerContainer from './messenger-container';
import OfferContainer from './offer-container';
import OrderContainer from './order-container';
import OrdersAllContainer from './orders/orders-all-container';
import OrdersCancelledContainer from './orders/orders-cancelled-container';
import OrdersCreatedContainer from './orders/orders-created-container';
import OrdersDeliveredContainer from './orders/orders-delivered-container';
import OrdersPaidContainer from './orders/orders-paid-container';
import OrdersRefundedContainer from './orders/orders-refunded-container';
import OrdersShippedContainer from './orders/orders-shipped-container';
import OrdersContainer from './orders-container';
import PacketContainer from './packet-container';
import PacketsContainer from './packets-container';
import PacketsVendorPacketaContainer from './packets-vendor-packeta-container';
import PacketsVendorsContainer from './packets-vendors-container';
import PostSuggestionsContainer from './post-suggestions-container';
import ProductContainer from './product-container';
import ProductsContainer from './products-container';
import PropertiesContainer from './properties-container';
import PropertyContainer from './property-container';
import ReportAccountingContainer from './report-accounting-container';
import ReportCategoryLanguagesContainer from './report-category-languages-container';
import ReportContainer from './report-container';
import ReportConversionFunnelContainer from './report-conversion-funnel-container';
import ReportOssContainer from './report-oss-container';
import ReportPhotoDuplicatesContainer from './report-photo-duplicates-container';
import ReportProductClaimsContainer from './report-product-claims-container';
import ReportProductLanguagesContainer from './report-product-languages-container';
import ReportProductMetaDescriptionContainer from './report-product-meta-description-container';
import ReportProductMetaKeywordsContainer from './report-product-meta-keywords-container';
import ReportProductSafetyContainer from './report-product-safety-container';
import ReportProductScoringContainer from './report-product-scoring-container';
import ReportPropertyLanguagesContainer from './report-property-languages-container';
import ReportPropertyValueLanguagesContainer from './report-property-value-languages-container';
import ReportWarehouseCategoryTrendContainer from './report-warehouse-category-trend-container';
import ReportWarehouseProductPhotoComparisonContainer from './report-warehouse-product-photo-comparison-container';
import ReportWarehouseProductToImportContainer from './report-warehouse-product-to-import-container';
import ReportWarehouseProductTrendContainer from './report-warehouse-product-trend-container';
import ReportWarehouseProductsPairingContainer from './report-warehouse-products-pairing-container';
import ResponsiblePersonsContainer from './responsible-persons-container';
import SearchContainer from './search-container';
import SettingsContainer from './settings-container';
import SettingsCountriesContainer from './settings-countries-container';
import SettingsCountryContainer from './settings-country-container';
import SettingsCountryLanguagesContainer from './settings-country-languages-container';
import SettingsHolidayPeriodsContainer from './settings-holiday-periods-container';
import SettingsTagsContainer from './settings-tags-container';
import SocialNetworksContainer from './social-networks-container';
import WarehouseOrderAuditLogContainer from './warehouse-order-audit-log-container';
import WarehouseOrderContainer from './warehouse-order-container';
import WarehouseOrderReportContainer from './warehouse-order-report-container';
import WarehouseOrderUnpairedProductsContainer from './warehouse-order-unpaired-products-container';
import WarehouseOrdersContainer from './warehouse-orders-container';
import WebAnalyticsInsightsContainer from './web-analytics-insights-container';
import WebAnalyticsRecommendationsContainer from './web-analytics-recommendations-container';
import WithdrawalContainer from './withdrawal-container';
import WithdrawalNewContainer from './withdrawal-new-container';
import WithdrawalsContainer from './withdrawals-container';

const { Header, Footer } = Layout;

const styleLayout: CSSProperties = {
  minHeight: '100vh',
};

const styleFooter: CSSProperties = {
  textAlign: 'center',
};

function LayoutContainer({ socket }: { socket?: Socket }): React.ReactElement {
  const dispatch = useAppDispatch();
  const userIsLoggedIn = useSelector((s: RootState) => s.User.isLoggedIn);

  useEffect((): void => {
    if (userIsLoggedIn) {
      dispatch(withdrawalReceivedCount());
    }
  }, [dispatch, userIsLoggedIn]);

  return (
    <ConfigProvider locale={antdLocale}>
      <Layout style={styleLayout}>
        <Header>
          <Topbar />
        </Header>
        <KeyboardShortcutsHelp />
        <ChunkErrorBoundary>
          <Routes>
            <Route
              path="/"
              element={
                <SiderContentComponent contentChildren={<HomeContainer />} />
              }
            />
            <Route
              path="/login"
              element={
                <SiderContentComponent contentChildren={<LoginContainer />} />
              }
            />
            <Route
              path="/logout"
              element={
                <SiderContentComponent contentChildren={<LogoutContainer />} />
              }
            />

            <Route
              element={
                <PrivateRoute isLoggedIn={userIsLoggedIn}>
                  <Outlet />
                </PrivateRoute>
              }
            >
              <Route
                element={<SiderContentComponent contentChildren={<Outlet />} />}
              >
                <Route path="dashboard" element={<DashboardContainer />} />
                <Route path="account" element={<AccountContainer />} />
                <Route
                  path="account/:accountUUID"
                  element={<AccountContainer />}
                />
                <Route
                  path="messenger"
                  element={<MessengerContainer socket={socket} />}
                />
                <Route path="discount" element={<DiscountContainer />} />
                <Route path="search" element={<SearchContainer />} />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarOffer />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="offer" element={<OfferContainer />} />
                <Route path="brand" element={<BrandContainer />} />
                <Route
                  path="brand/:brandUUID"
                  element={<BrandDetailContainer />}
                />
                <Route
                  path="manufacturers"
                  element={<ManufacturersContainer />}
                />
                <Route
                  path="responsible-persons"
                  element={<ResponsiblePersonsContainer />}
                />
                <Route path="categories" element={<CategoriesContainer />} />
                <Route
                  path="category/:categoryUUID"
                  element={<CategoryContainer />}
                />
                <Route path="properties" element={<PropertiesContainer />} />
                <Route
                  path="property/:propertyCode"
                  element={<PropertyContainer />}
                />
                <Route path="products" element={<ProductsContainer />} />
                <Route
                  path="product/:productUUID"
                  element={<ProductContainer socket={socket} />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarOrders />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="order/:orderUUID" element={<OrderContainer />} />
                <Route path="orders" element={<OrdersContainer />} />
                <Route path="orders/all" element={<OrdersAllContainer />} />
                <Route
                  path="orders/withdrawals"
                  element={<WithdrawalsContainer />}
                />
                <Route
                  path="orders/withdrawal/new"
                  element={<WithdrawalNewContainer />}
                />
                <Route
                  path="orders/withdrawal/:uuid"
                  element={<WithdrawalContainer />}
                />
                <Route
                  path="orders/cancelled"
                  element={<OrdersCancelledContainer />}
                />
                <Route
                  path="orders/created"
                  element={<OrdersCreatedContainer />}
                />
                <Route path="orders/paid" element={<OrdersPaidContainer />} />
                <Route
                  path="orders/refunded"
                  element={<OrdersRefundedContainer />}
                />
                <Route
                  path="orders/shipped"
                  element={<OrdersShippedContainer />}
                />
                <Route
                  path="orders/delivered"
                  element={<OrdersDeliveredContainer />}
                />
                <Route
                  path="orders/warehouse-orders"
                  element={<WarehouseOrdersContainer />}
                />
                <Route
                  path="orders/warehouse-orders/audit-log"
                  element={<WarehouseOrderAuditLogContainer />}
                />
                <Route
                  path="orders/warehouse-orders/report"
                  element={<WarehouseOrderReportContainer />}
                />
                <Route
                  path="orders/warehouse-orders/unpaired-products"
                  element={<WarehouseOrderUnpairedProductsContainer />}
                />
                <Route
                  path="orders/warehouse-order/:uuid"
                  element={<WarehouseOrderContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarPackets />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="packets" element={<PacketsContainer />} />
                <Route path="packet/:packetId" element={<PacketContainer />} />
                <Route
                  path="packets/vendors"
                  element={<PacketsVendorsContainer />}
                />
                <Route
                  path="packets/vendor/packeta"
                  element={<PacketsVendorPacketaContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarDownloader />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="downloader" element={<DownloaderContainer />} />
                <Route
                  path="downloader/products"
                  element={<DownloaderProductsContainer />}
                />
                <Route
                  path="downloader/product/:productDownloadedUUID"
                  element={<DownloaderProductContainer />}
                />
                <Route
                  path="downloader/warehouse-categories"
                  element={<DownloaderWarehouseCategories />}
                />
                <Route
                  path="downloader/warehouse-category/:uuid"
                  element={<DownloaderWarehouseCategory />}
                />
                <Route
                  path="downloader/warehouse-properties"
                  element={<DownloaderWarehouseProperties />}
                />
                <Route
                  path="downloader/warehouse-property/:rawName"
                  element={<DownloaderWarehouseProperty />}
                />
                <Route
                  path="downloader/warehouse-products"
                  element={<DownloaderWarehouseProducts />}
                />
                <Route
                  path="downloader/warehouse-product/:uuid"
                  element={<DownloaderWarehouseProduct socket={socket} />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarEbay />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="ebay" element={<EbayContainer />} />
                <Route
                  path="ebay/marketplaces"
                  element={<EbayMarketplacesContainer />}
                />
                <Route
                  path="ebay/marketplace/:code"
                  element={<EbayMarketplaceContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarMarketing />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route
                  path="marketing"
                  element={<PostSuggestionsContainer />}
                />
                <Route
                  path="marketing/suggestions"
                  element={<PostSuggestionsContainer />}
                />
                <Route
                  path="marketing/settings"
                  element={<SocialNetworksContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarReport />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="report" element={<ReportContainer />} />
                <Route
                  path="report/category-languages"
                  element={<ReportCategoryLanguagesContainer />}
                />
                <Route
                  path="report/product-languages"
                  element={<ReportProductLanguagesContainer />}
                />
                <Route
                  path="report/product-meta-description"
                  element={<ReportProductMetaDescriptionContainer />}
                />
                <Route
                  path="report/product-meta-keywords"
                  element={<ReportProductMetaKeywordsContainer />}
                />
                <Route
                  path="report/product-scoring"
                  element={<ReportProductScoringContainer />}
                />
                <Route
                  path="report/property-languages"
                  element={<ReportPropertyLanguagesContainer />}
                />
                <Route
                  path="report/property-value-languages"
                  element={<ReportPropertyValueLanguagesContainer />}
                />
                <Route
                  path="report/warehouse-product-to-import"
                  element={<ReportWarehouseProductToImportContainer />}
                />
                <Route
                  path="report/warehouse-product-trend"
                  element={<ReportWarehouseProductTrendContainer />}
                />
                <Route
                  path="report/warehouse-category-trend"
                  element={<ReportWarehouseCategoryTrendContainer />}
                />
                <Route
                  path="report/warehouse-products-pairing"
                  element={<ReportWarehouseProductsPairingContainer />}
                />
                <Route
                  path="report/photo-duplicates"
                  element={<ReportPhotoDuplicatesContainer />}
                />
                <Route
                  path="report/product-safety"
                  element={<ReportProductSafetyContainer />}
                />
                <Route
                  path="report/product-claims"
                  element={<ReportProductClaimsContainer />}
                />
                <Route
                  path="report/warehouse-product-photo-comparison"
                  element={<ReportWarehouseProductPhotoComparisonContainer />}
                />
                <Route
                  path="report/accounting"
                  element={<ReportAccountingContainer />}
                />
                <Route path="report/oss" element={<ReportOssContainer />} />
                <Route
                  path="report/conversion-funnel"
                  element={<ReportConversionFunnelContainer />}
                />
                <Route
                  path="report/web-analytics-insights"
                  element={<WebAnalyticsInsightsContainer />}
                />
                <Route
                  path="web-analytics/recommendations"
                  element={<WebAnalyticsRecommendationsContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarSettings />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="settings" element={<SettingsContainer />} />
                <Route
                  path="settings/countries"
                  element={<SettingsCountriesContainer />}
                />
                <Route
                  path="settings/country/:isoCode"
                  element={<SettingsCountryContainer />}
                />
                <Route
                  path="settings/country-languages"
                  element={<SettingsCountryLanguagesContainer />}
                />
                <Route
                  path="settings/tags"
                  element={<SettingsTagsContainer />}
                />
                <Route
                  path="settings/holiday-periods"
                  element={<SettingsHolidayPeriodsContainer />}
                />
              </Route>

              <Route
                element={
                  <SiderContentComponent
                    siderChildren={<SidebarCRM />}
                    contentChildren={<Outlet />}
                  />
                }
              >
                <Route path="crm" element={<CrmCustomersContainer />} />
                <Route
                  path="crm/customers"
                  element={<CrmCustomersContainer />}
                />
                <Route
                  path="crm/customer/:customerUUID"
                  element={<CrmCustomerDetailContainer />}
                />
                <Route path="crm/watchdog" element={<CrmWatchdogContainer />} />
                <Route
                  path="crm/gift-registries"
                  element={<GiftRegistryContainer />}
                />
                <Route
                  path="crm/marketing-consent-campaign"
                  element={<MarketingConsentCampaignContainer />}
                />
              </Route>
            </Route>
          </Routes>
        </ChunkErrorBoundary>
        <Footer style={styleFooter}>wawahi s.r.o.</Footer>
      </Layout>
    </ConfigProvider>
  );
}

export default LayoutContainer;
